import { AlertTriangle, CalendarX2, Clock, UserX } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { adminButton } from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import type { ScheduleClashPrompt } from '@/hooks/useScheduleClashCheck';
import type { ScheduleWarningKind } from '@/lib/schedule-clash';

const ICONS: Record<ScheduleWarningKind, typeof AlertTriangle> = {
  double_booking: AlertTriangle,
  buffer: Clock,
  time_off: UserX,
  outside_availability: UserX,
  outside_opening_hours: CalendarX2,
};

/**
 * The one prompt shown before a job is booked, moved or assigned into a clash.
 * Nothing is saved until the owner chooses "Book anyway".
 */
export function ScheduleClashDialog({ open, headline, warnings, repeating, onBookAnyway, onPickAnother }: ScheduleClashPrompt) {
  const serious = warnings.some((w) => w.kind === 'double_booking');
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onPickAnother()}>
      <AlertDialogContent className="max-w-md" data-testid="schedule-clash-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-[17px]">
            <AlertTriangle className={cn('h-5 w-5 shrink-0', serious ? 'text-destructive' : 'text-amber-500')} aria-hidden="true" />
            {headline}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {serious ? 'This would double-book someone.' : 'Worth a second look before you save.'} You can still book it if you've already sorted it with your team.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-2" aria-label="Clashes">
          {warnings.map((w, i) => {
            const Icon = ICONS[w.kind];
            return (
              <li
                key={`${w.kind}-${w.memberId ?? ''}-${w.jobId ?? i}`}
                className={cn(
                  'flex items-start gap-2.5 rounded-lg border px-3 py-2 text-[13px] text-foreground',
                  w.kind === 'double_booking'
                    ? 'border-destructive/30 bg-destructive/5'
                    : 'border-amber-500/30 bg-amber-500/5',
                )}
              >
                <Icon
                  className={cn('mt-0.5 h-4 w-4 shrink-0', w.kind === 'double_booking' ? 'text-destructive' : 'text-amber-600 dark:text-amber-400')}
                  aria-hidden="true"
                />
                <span>{w.message}</span>
              </li>
            );
          })}
        </ul>
        {repeating && (
          <p className="text-[12.5px] text-muted-foreground">
            This only covers the first visit. Any later visits that clash are held back for you on the Repeat cleans page.
          </p>
        )}
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <AlertDialogCancel className={cn(adminButton.outline, 'mt-0')} onClick={onPickAnother}>
            Pick another time
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(adminButton.primary, serious && 'bg-destructive text-destructive-foreground hover:bg-destructive/90')}
            onClick={onBookAnyway}
          >
            Book anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
